import React, {useState, useEffect} from 'react';
import {Modal, Form, Row, Col} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {useAlert} from '@/components/alerts';
import axios from 'axios';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import {extractErrorMessage} from '@/utils/errorHandling';

interface Role {
    id: number;
    name: string;
    description?: string;
    user_count: number;
    permission_count: number;
    created_on: string | null;
    updated_on: string | null;
}

interface RoleModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    activeRole: Role | null;
}

const RoleModal: React.FC<RoleModalProps> = ({modalOpen, closeModal, activeRole}) => {
    const {showSuccess, showError} = useAlert();
    const [loading, setLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const isEditing = activeRole !== null;

    useEffect(() => {
        if (activeRole) {
            setFormData({
                name: activeRole.name,
                description: activeRole.description || '',
            });
        } else {
            setFormData({
                name: '',
                description: '',
            });
        }
        setErrors({});
    }, [activeRole, modalOpen]);

    const handleInputChange = (field: string, value: string): void => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = i18n.t('role_modal_name_required.text');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = i18n.t('role_modal_name_min_length.text');
        } else if (formData.name.trim().length > 50) {
            newErrors.name = i18n.t('role_modal_name_max_length.text');
        }

        
        if (formData.description && formData.description.length > 255) {
            newErrors.description = i18n.t('role_modal_description_max_length.text');
        }
        else if (formData.description && formData.description.length < 20) { 
            newErrors.description = i18n.t('role_modal_description_min_length.text');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const payload = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
            };

            if (isEditing && activeRole) {
                await axios.put(buildURL('adminRoleDetail', {role_id: activeRole.id}), payload);
                showSuccess(i18n.t('role_modal_update_success.text', {roleName: payload.name}));
            } else {
                await axios.post(window.URLS.adminRolesList, payload);
                showSuccess(i18n.t('role_modal_create_success.text', {roleName: payload.name}));
            }

            closeModal();
        } catch (error: any) {
            console.error('Failed to save role:', error);
            
            if (error.response?.status === 400 && error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                const errorMessage = extractErrorMessage(
                    error,
                    i18n.t(isEditing ? 'role_modal_update_error.text' : 'role_modal_create_error.text', {error: ''})
                );
                showError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (): void => {
        if (!loading) {
            closeModal();
        }
    };

    return (
        <Modal show={modalOpen} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditing ? i18n.t('role_modal_edit_title.text', {roleName: activeRole?.name}) : i18n.t('role_modal_create_title.text')}
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    {i18n.t('role_modal_name_label.text')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder={i18n.t('role_modal_name_placeholder.text')}
                                    isInvalid={!!errors.name}
                                    disabled={loading}
                                    maxLength={50}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    {i18n.t('role_modal_name_help.text')}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>{i18n.t('roles_description.text')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder={i18n.t('roles_enter_role_description_optional.text')}
                                    isInvalid={!!errors.description}
                                    disabled={loading}
                                    maxLength={255}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.description}
                                </Form.Control.Feedback>
                                <Form.Text className="text-muted">
                                    {i18n.t('role_modal_description_help.text')}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    {isEditing && activeRole && (
                        <Row>
                            <Col md={12}>
                                <div className="alert alert-info">
                                    <strong>{i18n.t('role_modal_current_statistics.text')}:</strong>
                                    <ul className="mb-0 mt-2">
                                        <li>{i18n.t('role_modal_users_assigned.text', {count: activeRole.user_count})}</li>
                                        <li>{i18n.t('role_modal_permissions_granted.text', {count: activeRole.permission_count})}</li>
                                    </ul>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        {i18n.t('jt_cancel.text')}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={loading}
                        disabled={loading}
                    >
                        {isEditing ? i18n.t('role_modal_update_button.text') : i18n.t('roles_create_role.text')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default RoleModal;
